import { useModelingListQuery } from '@/hook';
import { Close, OpenInNew, VDialog, VFlex, VStack, cn } from '@/package';
import type { Modeling } from '@/type';

const TrainingDataModal = ({
  modelingDetail,
  visible,
  onUpdateVisible,
}: {
  modelingDetail: Modeling;
  visible: boolean;
  onUpdateVisible(visible: boolean): void;
}) => {
  const { alias, taskInfo, parentID, epoch, deployStatus } = modelingDetail;
  const { data: modelList } = useModelingListQuery({
    versionType: 'Vueron',
  });
  const moveNewTab = (link: string) => {
    window.open(`${window.location.origin}${link}`, '_blank');
  };

  return (
    <VDialog
      dialogMainClass={cn('box-content w-[324px] bg-white p-6')}
      visible={visible}
    >
      <VStack className="w-full" gap={1}>
        <VFlex justify="between" items="center">
          <div className="text-headline-sm text-blue-300">
            <strong>Training data</strong>
          </div>
          <Close
            role="button"
            className="size-6 text-gray-400"
            onClick={() => onUpdateVisible(false)}
          />
        </VFlex>

        <VStack className="divide-y divide-gray-300 text-body-md text-gray-500">
          <VFlex className="px-2 py-1" items="center" justify="between">
            <span className="w-[108px]">Alias</span>
            <div className="flex-1 text-black">{alias ? alias : '-'}</div>
          </VFlex>

          <VFlex className="px-2 py-1" items="center" justify="between">
            <span className="w-[108px]">Task ID &#40;%&#41;</span>
            <div className="max-h-[220px] flex-1 overflow-y-auto text-primary-500">
              <VStack gap={0.25}>
                {taskInfo?.map(task => {
                  return (
                    <VFlex
                      key={task.taskID}
                      className="py-1"
                      justify="between"
                      items="center"
                    >
                      <span>
                        {task.taskID} &#40;{task.trainingRatio}%&#41;
                      </span>
                      <OpenInNew
                        role="button"
                        className="size-4 text-primary-500"
                        onClick={() =>
                          moveNewTab(
                            `/project/${task.projectID}?selectedTaskId=${task.taskID}`,
                          )
                        }
                      />
                    </VFlex>
                  );
                })}
              </VStack>
            </div>
          </VFlex>

          <VFlex className="px-2 py-1" items="center" justify="between">
            <span className="w-[108px]">Parent ID</span>
            <div className="flex-1 text-primary-500">
              <VFlex justify="between" items="center">
                <span>
                  {parentID < 1000
                    ? modelList?.find(modeling => modeling.id === parentID)
                        ?.name
                    : parentID}
                </span>
                {parentID >= 1000 && (
                  <OpenInNew
                    role="button"
                    className="size-4 text-primary-500"
                    onClick={() => moveNewTab(`/modeling/${parentID}`)}
                  />
                )}
              </VFlex>
            </div>
          </VFlex>

          <VFlex className="px-2 py-1" items="center" justify="between">
            <span className="w-[108px]">Epoch</span>
            <div className="flex-1 text-black">{epoch}</div>
          </VFlex>

          <VFlex className="px-2 py-1" items="center" justify="between">
            <span className="w-[108px]">Deply status</span>
            <div
              className={cn('flex-1', {
                'text-status-notStarted':
                  deployStatus !== 'Deploying' &&
                  deployStatus !== 'Deployed' &&
                  deployStatus !== 'DeployFailure',
                'text-status-inProgress': deployStatus === 'Deploying',
                'text-status-completed': deployStatus === 'Deployed',
              })}
            >
              <strong>
                {deployStatus === 'Deploying'
                  ? 'In progress'
                  : deployStatus === 'Deployed'
                    ? 'Deployed'
                    : deployStatus === 'DeployFailure'
                      ? 'Failed'
                      : deployStatus === 'DeployPending'
                        ? 'Pending'
                        : 'Not started'}
              </strong>
            </div>
          </VFlex>
        </VStack>
      </VStack>
    </VDialog>
  );
};

TrainingDataModal.displayName = 'TrainingDataModal';

export default TrainingDataModal;
